/**
 * BatchGenerationFormV2 - Full-width batch generation form
 *
 * Supports both Unified Mode (apply same settings to all images) and
 * Matrix Mode (per-row configuration with flexible row management).
 */

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Sparkles, Loader2 } from 'lucide-react';

import { Button } from '@/components/v2/ui';
import { BatchImageUploader } from './BatchImageUploader';
import { BatchWorkspaceGallery } from './BatchWorkspaceGallery';
import { BatchUnifiedConfig } from './BatchUnifiedConfig';
import { BatchHeader, DiscountBanner } from './BatchFormHeader';
import { BatchMatrixModeToggle, BatchMatrixConfig } from './matrix';
import { useBatchFormState } from './useBatchFormState';

interface BatchGenerationFormV2Props {
  onSuccess?: () => void;
}

export function BatchGenerationFormV2({ onSuccess }: BatchGenerationFormV2Props) {
  const [searchParams] = useSearchParams();

  const initialProductIds = useMemo(() => {
    const ids = searchParams.get('products');
    if (!ids) return undefined;
    return ids.split(',').filter(Boolean).slice(0, 50);
  }, [searchParams]);

  const initialProductNames = useMemo(() => {
    const names = searchParams.get('names');
    if (!names || !initialProductIds) return undefined;
    const nameList = names.split(',');
    const map: Record<string, string> = {};
    initialProductIds.forEach((id, i) => {
      if (nameList[i]) map[id] = decodeURIComponent(nameList[i]);
    });
    return map;
  }, [searchParams, initialProductIds]);

  const form = useBatchFormState({ onSuccess, initialProductIds, initialProductNames });

  return (
    <div className="space-y-6">
      {/* Header section - centered */}
      <div className="max-w-4xl mx-auto space-y-4">
        <BatchHeader discount={form.discount} />
        <DiscountBanner discount={form.discount} />

        <BatchMatrixModeToggle
          enabled={form.isMatrixMode}
          onChange={form.setIsMatrixMode}
          disabled={form.isPending}
        />
      </div>

      {form.isMatrixMode ? (
        /* Matrix Mode - wider layout for row configuration */
        <div className="max-w-6xl mx-auto">
          <BatchMatrixConfig
            items={form.matrixItems}
            templates={form.templates}
            collections={form.collections}
            costPerImage={form.costPerImage}
            costPerImage4k={form.costPerImage4k}
            discountPercent={form.discount}
            onChange={form.setMatrixItems}
            onOpenGallery={form.openGalleryForRow}
            onDuplicateRow={form.duplicateRow}
            disabled={form.isPending}
            globalMatrixSettings={form.globalMatrixSettings}
            onGlobalSettingsChange={form.handleGlobalSettingsChange}
            onApplyGlobalToAll={form.applyGlobalToAll}
            showApplyConfirmation={form.showApplyConfirmation}
            onConfirmApply={form.applyGlobalToAll}
            onCancelApply={() => form.setShowApplyConfirmation(false)}
            getItemGenerations={form.getItemGenerations}
            collectionCountCache={form.collectionCountCache}
            cacheCollectionCount={form.cacheCollectionCount}
          />
        </div>
      ) : (
        /* Unified Mode - centered layout */
        <div className="max-w-4xl mx-auto space-y-6">
          <BatchImageUploader
            files={form.files}
            onFilesChange={form.setFiles}
            selectedImages={form.selectedImages}
            onSelectedImagesChange={form.setSelectedImages}
            onOpenGallery={() => form.setShowGallery(true)}
            maxImages={10}
            disabled={form.isPending}
          />

          <BatchUnifiedConfig
            mode={form.mode}
            onModeChange={form.setMode}
            templateId={form.templateId}
            onTemplateChange={form.setTemplateId}
            collectionId={form.collectionId}
            onCollectionChange={form.setCollectionId}
            customPrompt={form.customPrompt}
            onCustomPromptChange={form.setCustomPrompt}
            variants={form.variants}
            onVariantsChange={form.setVariants}
            aspectRatio={form.aspectRatio}
            onAspectRatioChange={form.setAspectRatio}
            templates={form.templates}
            collections={form.collections}
            collectionImageCount={form.collectionImageCount}
            inputCount={form.inputCount}
            totalGenerations={form.totalGenerations}
            costPerImage={form.costPerImage}
            totalCost={form.totalCost}
            discountPercent={form.discount}
            disabled={form.isPending}
          />
        </div>
      )}

      {/* Submit button - centered */}
      <div className="max-w-4xl mx-auto">
        <Button
          variant="primary"
          onClick={form.handleSubmit}
          disabled={!form.canSubmit || form.isPending}
          className="w-full bg-orange-500 hover:bg-orange-600 py-3 text-lg"
          iconLeft={
            form.isPending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Sparkles className="h-5 w-5" />
            )
          }
        >
          {form.isPending ? 'Wird gestartet...' : 'Batch starten'}
        </Button>
      </div>

      <BatchWorkspaceGallery
        isOpen={form.showGallery}
        onClose={() => form.setShowGallery(false)}
        selectedImages={form.selectedImages}
        onSelect={form.setSelectedImages}
        maxSelect={form.isMatrixMode ? 1 : 10 - form.files.length}
      />
    </div>
  );
}
